import React, { useState, useEffect } from 'react'
import {
  VisibilityOutlined,
  VisibilityOffOutlined,
  WarningOutlined
} from '@mui/icons-material'
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  InputLabel
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import theme from '../../theme'
import InputErrorMessage from '../../InputErrorMessage'

type FormValues = {
  email: string
  password: string
}

interface Props {
  onSubmit: (data: FormValues) => void
  showLoginFailedMessage: boolean
  hasPermissions: boolean
  onFormChange?: () => void
}

const LoginForm = ({
  onSubmit,
  onFormChange,
  showLoginFailedMessage,
  hasPermissions
}: Props) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<FormValues>({
    mode: 'onSubmit'
  })

  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (!onFormChange) return

    const subscription = watch((_, { type }) => {
      if (type === 'change') {
        onFormChange()
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, onFormChange])

  const watchedEmail = watch('email')
  const watchedPassword = watch('password')

  const handleFormSubmit = (data: FormValues) => {
    onSubmit(data)
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  const showNoPermissionsMessage =
    !hasPermissions &&
    !showLoginFailedMessage &&
    !errors.email &&
    !errors.password &&
    !watchedEmail &&
    !watchedPassword

  return (
    <Box display="flex" flexDirection="column" gap={theme.space['32']}>
      <Box
        display="flex"
        flexDirection="row"
        gap={theme.space['20']}
        alignItems="center"
      >
        <img
          src="/images/sign-in-icon.svg"
          alt="GTV logo"
          style={{
            width: '34px',
            height: '34px'
          }}
        />
        <Typography variant="heading-8">Sign In</Typography>
      </Box>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          gap={theme.space['24']}
          padding={theme.space['40']}
          borderRadius={theme.borders['rounded-10']}
          sx={{
            backgroundColor: theme.palette['base-white']
          }}
        >
          <Box
            sx={{
              width: '100%'
            }}
          >
            <InputLabel
              id="email-label"
              sx={{
                marginBottom: theme.space['8']
              }}
            >
              <Typography
                variant="body-11"
                sx={{ color: theme.palette['sage-900'], fontWeight: 500 }}
              >
                Email Address
              </Typography>
            </InputLabel>
            <Controller
              name="email"
              rules={{
                required: 'Please enter a valid email format',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Please enter a valid email format'
                }
              }}
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <TextField
                    {...field}
                    variant="outlined"
                    type="email"
                    placeholder="name@company.com"
                    error={!!error}
                    sx={{
                      width: '100%',
                      '& input': {
                        boxSizing: 'border-box',
                        height: '56px'
                      }
                    }}
                  />
                  {error && <InputErrorMessage error={error.message} />}
                </Box>
              )}
            />
          </Box>
          <Box
            sx={{
              width: '100%'
            }}
          >
            <InputLabel
              id="password-label"
              sx={{
                marginBottom: theme.space['8']
              }}
            >
              <Typography
                variant="body-11"
                sx={{ color: theme.palette['sage-900'], fontWeight: 500 }}
              >
                Password
              </Typography>
            </InputLabel>
            <Controller
              name="password"
              rules={{
                required: 'Please provide a password'
              }}
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <TextField
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    error={!!error}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOutlined fontSize="small" />
                            ) : (
                              <VisibilityOffOutlined fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      width: '100%',
                      '& input': {
                        boxSizing: 'border-box',
                        height: '56px'
                      }
                    }}
                  />
                  {error && <InputErrorMessage error={error.message} />}
                </Box>
              )}
            />
          </Box>
        </Box>
        <Button
          data-testid="sumbit-login"
          variant="contained"
          type="submit"
          sx={{
            height: '56px',
            width: '100%',
            boxShadow: 'none',
            marginTop: theme.space['48']
          }}
        >
          <Typography variant="ui-3">Sign in</Typography>
        </Button>
      </form>
      {showLoginFailedMessage && (
        <Box
          sx={{
            marginTop: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <WarningOutlined
            color="error"
            fontSize="inherit"
            sx={{ marginRight: '5px' }}
          />
          <Typography color="error" variant="body2">
            The username or password is incorrect. Please try again.
          </Typography>
        </Box>
      )}
      {showNoPermissionsMessage && (
        <Box
          sx={{
            marginTop: '30px',
            display: 'flex',
            alignItems: 'top',
            justifyContent: 'center'
          }}
        >
          <WarningOutlined
            color="error"
            fontSize="inherit"
            sx={{ marginRight: '15px' }}
          />
          <Typography color="error" variant="body2">
            The provided username is invalid. Please try again.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default LoginForm
